import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import UnifiedChatWidget from './components/UnifiedChatWidget';
import AppLoadingScreen from './components/AppLoadingScreen';
import Home from './pages/Home';
import ProductPage from './pages/Product';
import Category from './pages/Category';
import Checkout from './pages/Checkout';
import CartDrawer from './components/CartDrawer';
import FacebookPixel from './components/FacebookPixel';
import LivePurchaseNotifications from './components/LivePurchaseNotifications';
import CookieConsent from './components/CookieConsent';

import Cart from './pages/Cart';
import About from './pages/About';
import Contact from './pages/Contact';
import AdminDemo from './pages/AdminDemo';
import ThankYou from './pages/ThankYou';

import { SettingsProvider } from './context/SettingsContext';
import { dataPreloader, PreloadedData } from './utils/dataPreloader';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [preloadedData, setPreloadedData] = useState<PreloadedData | null>(null);

  useEffect(() => {
    // Start preloading critical data immediately
    const initializeApp = async () => {
      try {
        console.log('[App] Starting data preload...');
        const data = await dataPreloader.preload();
        setPreloadedData(data);
        console.log('[App] Preload complete');

        // Wait an additional moment to ensure UI has time to render
        // This prevents the flash of loading screen disappearing before content renders
        setTimeout(() => {
          setIsLoading(false);
        }, 800); // Small delay to ensure smooth transition and UI readiness
      } catch (error) {
        console.error('[App] Preload failed:', error);
        // Continue anyway with empty data
        setPreloadedData({
          settings: null,
          categories: [],
          featuredProducts: []
        });
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };

    initializeApp();
  }, []);

  // Show loading screen while critical data is being fetched AND UI is rendering
  if (isLoading || !preloadedData) {
    return <AppLoadingScreen />;
  }

  return (
    <SettingsProvider preloadedSettings={preloadedData.settings}>
      <FacebookPixel />
      <CookieConsent />
      <div className="min-h-screen bg-neutral-50 text-neutral-900 font-sans flex flex-col">
        <TopBar />
        <Header />
        <CartDrawer />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/category/:slug" element={<Category />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<AdminDemo />} />
            <Route path="/thank_you" element={<ThankYou />} />
          </Routes>
        </main>
        <Footer />
        <UnifiedChatWidget />
        <LivePurchaseNotifications />
      </div>
    </SettingsProvider>
  );
}

export default App;
